import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { WarningLine, DownSmallLine, UpSmallLine } from '@teivor/shared-ui';

import { useServiceHealthQuery, type ServiceHealthCheck } from '../features/admin/queries';

/**
 * Below-topbar strip that warns when production-required external services are
 * unconfigured. Polls /api/admin/service-health.
 *
 * Klaret: this is a WARNING (config incomplete), not a hard error — styled with
 * the warning tint + ochre fg + the MingCute warning glyph.
 *
 * NOTE: the backend service-health catalog still lists Stripe/billing as a
 * required service, but billing was removed in the lean rebuild — so we filter
 * those out here (and recompute the count) until the backend catalog drops them.
 */
const isRetiredBilling = (s: ServiceHealthCheck): boolean =>
  /stripe|billing|payment|invoic/i.test(`${s.key} ${s.category ?? ''} ${s.label ?? ''}`);

export function ServiceStatusBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const { data } = useServiceHealthQuery();
  const [expanded, setExpanded] = React.useState(false);

  React.useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  if (!data) return null;

  // Required + unconfigured, excluding the retired billing/Stripe entries.
  const required = data.services.filter(
    (s) => s.required && !s.configured && !isRetiredBilling(s),
  );
  if (required.length === 0) return null;
  const count = required.length;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="flex flex-col border-b border-[var(--tvl-warning)] bg-[var(--tvl-warning-tint)] text-[13px] leading-[18px] text-[var(--tvl-warning)]"
    >
      <div className="flex items-center gap-3 px-4 py-2">
        <WarningLine size={20} className="shrink-0" aria-hidden />
        <div className="flex-1">
          <span className="font-medium">
            {t('admin.service_health.banner.required', {
              defaultValue:
                '{{count}} production-required service(s) not configured — production functionality will fail.',
              count,
            })}
          </span>{' '}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="ml-1 inline-flex items-center gap-1 rounded-[var(--tvl-r1)] border border-[var(--tvl-warning)] px-2 py-0.5 text-[11px] font-medium hover:bg-[var(--tvl-wash-hover)]"
          >
            {expanded ? (
              <>
                {t('common.hide', { defaultValue: 'Hide details' })}
                <UpSmallLine size={14} aria-hidden />
              </>
            ) : (
              <>
                {t('common.show', { defaultValue: 'Show details' })}
                <DownSmallLine size={14} aria-hidden />
              </>
            )}
          </button>
        </div>
        <Link
          to="/admin/integrations"
          className="rounded-[var(--tvl-r1)] border border-[var(--tvl-warning)] px-2 py-0.5 text-[11px] hover:bg-[var(--tvl-wash-hover)]"
        >
          {t('admin.service_health.banner.go_configure', { defaultValue: 'Configure' })}
        </Link>
      </div>
      {expanded ? (
        <div className="border-t border-[var(--tvl-warning)] px-4 py-2 text-[11px] leading-[14px]">
          <ul className="space-y-0.5">
            {required.map((s) => (
              <li key={s.key} className="flex items-start gap-2 py-0.5">
                <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--tvl-warning)]" />
                <span className="font-medium">{s.label}</span>
                <span className="text-[var(--tvl-ink-3)]">— {s.reason ?? 'not configured'}</span>
                <span className="ml-auto rounded-[var(--tvl-r1)] bg-[var(--tvl-wash-pressed)] px-1 py-px text-[10px] uppercase tracking-wider text-[var(--tvl-ink-2)]">
                  {s.category}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="inline-flex items-center gap-1 rounded-[var(--tvl-r1)] border border-[var(--tvl-warning)] px-2 py-0.5 text-[11px] font-medium hover:bg-[var(--tvl-wash-hover)]"
            >
              {t('common.close', { defaultValue: 'Close' })}
              <UpSmallLine size={14} aria-hidden />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export type { ServiceHealthCheck };
